const REDIRECT_ERROR_MESSAGE = "Cannot add property type, object is not extensible";
const SENTRY_FLUSH_TIMEOUT = 1500;
const CONSTRUCTOR_SOURCE_LIMIT = 2000;

function isLoginTarget(target) {
    return target === "/login" || target.startsWith("/login?");
}

export function createLoginRedirectRecovery({
    resolveHref,
    flush,
    windowRef = typeof window !== "undefined" ? window : undefined
}) {
    const failedRedirects = new WeakMap();
    const pendingRedirects = new WeakMap();
    let recoveryStarted = false;

    function redirect(next, target, route) {
        // Vue Router puts this exact route object in the redirect error's
        // `from` field. Weak keys keep completed/concurrent navigations isolated.
        if (route && isLoginTarget(target)) pendingRedirects.set(route, target);
        try {
            next(target);
        } catch (error) {
            if (
                error instanceof TypeError &&
                error.message === REDIRECT_ERROR_MESSAGE &&
                isLoginTarget(target)
            ) {
                // Keep the destination outside the error: this environment may
                // also prevent adding properties to the thrown exception.
                failedRedirects.set(error, { target, reason: "non-extensible-error" });
            }
            // Let router.onError clear loading state and report the original
            // exception before attempting recovery.
            throw error;
        }
    }

    function getFailure(error) {
        const failure = failedRedirects.get(error);
        if (failure) return failure;

        // A replaced Error constructor can break instanceof Error, making the
        // router reject its own redirect signal instead of following it. Do not
        // trust type=2 alone: require the route and destination we issued.
        const target = pendingRedirects.get(error?.from);
        if (error?.type === 2 && target && error.to === target) {
            return { target, reason: "unrecognized-redirect" };
        }
    }

    function getDiagnostics(error) {
        const failure = getFailure(error);
        if (!failure) return undefined;
        const diagnostics = { reason: failure.reason };
        try {
            // Preserve the original exception/stack in Sentry. Snapshot only the
            // constructor, not page contents or globals, and bound wrapper code.
            Object.assign(diagnostics, {
                router_error_type: error.type,
                current_error_constructor_name: Error.name,
                current_error_constructor_source: Function.prototype.toString
                    .call(Error)
                    .slice(0, CONSTRUCTOR_SOURCE_LIMIT),
                error_constructor_name: Object.getPrototypeOf(error)?.constructor?.name,
                error_instanceof_current_error: error instanceof Error,
                error_prototype_matches_current_error:
                    Object.getPrototypeOf(error) === Error.prototype,
                error_extensible: Object.isExtensible(error)
            });
        } catch {
            // Even a hostile constructor/getter must not prevent reporting or
            // recovery. The original stack still identifies its creation site.
            diagnostics.constructor_diagnostics_unavailable = true;
        }
        return diagnostics;
    }

    async function recover(error) {
        const failure = getFailure(error);
        failedRedirects.delete(error);
        pendingRedirects.delete(error?.from);
        if (!failure || recoveryStarted || !windowRef) return;
        const { target } = failure;
        recoveryStarted = true;

        // A failed or stalled transport must not strand the user. Sentry's
        // timeout bounds its flush; our timer also covers an unsettled promise.
        let timer;
        try {
            await Promise.race([
                Promise.resolve().then(() => flush(SENTRY_FLUSH_TIMEOUT)),
                new Promise((resolve) => {
                    timer = setTimeout(resolve, SENTRY_FLUSH_TIMEOUT);
                })
            ]);
        } catch {
            // The exception was already captured; delivery is best effort.
        } finally {
            clearTimeout(timer);
        }

        // replaceState + reload forces a new document even for hash history.
        // A hash-only location.replace would re-enter the broken router.
        windowRef.history.replaceState(null, "", resolveHref(target));
        windowRef.location.reload();
    }

    return { redirect, recover, getDiagnostics };
}
